import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { dashboardApi } from '@/services/api';
import {
  HiOutlineUsers,
  HiOutlineUserGroup,
  HiOutlineCalendar,
  HiOutlineExclamation,
} from 'react-icons/hi';

interface Stats {
  clients: { total: number; active: number };
  staff: { total: number };
  attendance: { today: number; monthlyTotal: number };
}

interface Alert {
  type: string;
  severity: string;
  title: string;
  message: string;
  clientId: string;
}

export default function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useQuery<Stats>({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => dashboardApi.stats() as Promise<Stats>,
  });

  const { data: alertsData } = useQuery<{ alerts: Alert[] }>({
    queryKey: ['dashboard', 'alerts'],
    queryFn: () => dashboardApi.alerts() as Promise<{ alerts: Alert[] }>,
  });

  const alerts = alertsData?.alerts || [];

  const statCards = [
    {
      name: '利用者数',
      value: stats?.clients.total || 0,
      icon: HiOutlineUsers,
      color: 'bg-blue-500',
    },
    {
      name: '職員数',
      value: stats?.staff.total || 0,
      icon: HiOutlineUserGroup,
      color: 'bg-green-500',
    },
    {
      name: '本日の出席',
      value: stats?.attendance.today || 0,
      icon: HiOutlineCalendar,
      color: 'bg-purple-500',
    },
    {
      name: '今月の出席合計',
      value: stats?.attendance.monthlyTotal || 0,
      icon: HiOutlineCalendar,
      color: 'bg-orange-500',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">ダッシュボード</h1>
        <p className="text-gray-600 mt-1">本日の概要をご確認ください</p>
      </div>

      {/* 統計カード */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div key={stat.name} className="card">
            <div className="card-body flex items-center gap-4">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statsLoading ? '-' : stat.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* アラート */}
      {alerts.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <HiOutlineExclamation className="w-5 h-5 text-yellow-500" />
              通知・アラート
            </h2>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {alerts.map((alert, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                >
                  <HiOutlineExclamation className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-800">{alert.title}</p>
                    <p className="text-sm text-yellow-700">{alert.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* クイックアクション */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Link to="/attendance" className="card hover:shadow-md transition-shadow">
          <div className="card-body">
            <h3 className="font-semibold text-gray-900">出欠入力</h3>
            <p className="text-sm text-gray-600 mt-1">本日の出欠を記録する</p>
          </div>
        </Link>
        <Link to="/records" className="card hover:shadow-md transition-shadow">
          <div className="card-body">
            <h3 className="font-semibold text-gray-900">サービス記録</h3>
            <p className="text-sm text-gray-600 mt-1">支援内容を記録する</p>
          </div>
        </Link>
        <Link to="/clients/new" className="card hover:shadow-md transition-shadow">
          <div className="card-body">
            <h3 className="font-semibold text-gray-900">利用者登録</h3>
            <p className="text-sm text-gray-600 mt-1">新しい利用者を登録する</p>
          </div>
        </Link>
      </div>
    </div>
  );
}


